import { CatalogRendererProps } from '@copilotkit/a2ui-renderer';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  CustomerListColumn,
  customerListColumnsSchema,
  customerListMappingValueSchema,
  CustomerListMatchingLimits,
  customerListMatchingLimitsSchema,
  parseJsonProp,
} from '~/utils/customerListMatchingRules';

import UnavailableFormField from '../UnavailableFormField';
import { FallbackCard } from '../visitiqCatalog.styles';
import CustomView from './CustomView';
import RulesDisplay from './RulesDisplay';
import SimpleView from './SimpleView';
import { MatchingRulesView, useMatchingRulesState } from './useMatchingRulesState';

interface MatchingRulesRendererProps {
  label?: string;
  columns?: string;
  attachmentId?: string;
  limits?: string;
  initialView?: MatchingRulesView;
  displayOnly?: boolean;
  value?: string;
  setValue: (value: string) => void;
}

/**
 * `VIQMatchingRules`: the Customer List mapping form's rules, or, with `displayOnly`, the submitted rules read-only on
 * the confirmation card.
 */
const VisitiqMatchingRules = ({ props }: CatalogRendererProps<MatchingRulesRendererProps>): JSX.Element => {
  const { t } = useTranslation();

  if (props.displayOnly) {
    const value = parseJsonProp(customerListMappingValueSchema, props.value);
    return value ? (
      <RulesDisplay label={props.label} value={value} />
    ) : (
      <FallbackCard>{t('aiInsights.a2ui.matchingRules.unavailable')}</FallbackCard>
    );
  }

  const columns = parseJsonProp(customerListColumnsSchema, props.columns);
  const limits = parseJsonProp(customerListMatchingLimitsSchema, props.limits);
  if (!columns?.length || !limits || !props.attachmentId) {
    return <UnavailableFormField message={t('aiInsights.a2ui.matchingRules.unavailable')} setValue={props.setValue} />;
  }
  return (
    // A new file starts a new form: its columns, types and rules share nothing with the previous one's
    <MatchingRulesEditor
      key={props.attachmentId}
      label={props.label}
      attachmentId={props.attachmentId}
      columns={columns}
      limits={limits}
      initialView={props.initialView}
      value={props.value}
      setValue={props.setValue}
    />
  );
};

interface MatchingRulesEditorProps {
  label?: string;
  attachmentId: string;
  columns: CustomerListColumn[];
  limits: CustomerListMatchingLimits;
  initialView?: MatchingRulesView;
  value?: string;
  setValue: (value: string) => void;
}

const MatchingRulesEditor = ({ label, ...options }: MatchingRulesEditorProps): JSX.Element => {
  const state = useMatchingRulesState(options);

  return state.view === 'simple' ? (
    <SimpleView
      label={label}
      columns={options.columns}
      limits={options.limits}
      types={state.types}
      rules={state.rules}
      issues={state.issues}
      changedColumns={state.changedColumns}
      warningColumn={state.warningColumn}
      onTypeChange={state.setType}
      onToggleColumn={state.toggleSimpleColumn}
      onConfirmWarning={state.confirmWarning}
      onCancelWarning={state.cancelWarning}
      onCustomize={state.customize}
    />
  ) : (
    <CustomView
      columns={options.columns}
      limits={options.limits}
      types={state.types}
      rules={state.rules}
      issues={state.issues}
      expandedRule={state.expandedRule}
      rulesToDrop={state.rulesToDrop}
      onExpandRule={state.setExpandedRule}
      onTypeChange={state.setType}
      onToggleColumn={state.toggleColumn}
      onAddRule={state.addRule}
      onDeleteRule={state.deleteRule}
      onBackToSimple={state.backToSimple}
      onConfirmBackToSimple={state.confirmBackToSimple}
      onCancelBackToSimple={state.cancelBackToSimple}
    />
  );
};

export default VisitiqMatchingRules;
